"use client"

import { useOurContext } from "@/context/dot-field.context"
import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useInView } from "react-intersection-observer"

import { EliasSignoff } from "@/components/elias-signoff/elias-signoff"
import { PageContainer } from "@/components/page-container/page-container"
import { Statement } from "@/components/statement/statement"

import { useCallback, useEffect, useRef, useState } from "react"

gsap.registerPlugin(useGSAP)
gsap.registerPlugin(ScrollTrigger)

export default function Home() {
  const { dotFieldIndex, setDotFieldIndex } = useOurContext()
  const ref1 = useRef<HTMLDivElement>(null)
  const ref2 = useRef<HTMLDivElement>(null)
  const ref3 = useRef<HTMLDivElement>(null)
  const ref4 = useRef<HTMLDivElement>(null)
  const ref5 = useRef<HTMLDivElement>(null)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [initialLoad, setInitialLoad] = useState(true)

  // Create useInView hooks for each ref
  const { ref: inViewRef1, inView: isInView1Original } = useInView({
    trackVisibility: true,
    delay: 100,
    rootMargin: "-44% 0% -44% 0%",
    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
  })

  const { ref: inViewRef2, inView: isInView2 } = useInView({
    trackVisibility: true,
    delay: 100,
    rootMargin: "-44% 0% -44% 0%",
    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
  })

  const { ref: inViewRef3, inView: isInView3 } = useInView({
    trackVisibility: true,
    delay: 100,
    rootMargin: "-44% 0% -44% 0%",
    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
  })

  const { ref: inViewRef4, inView: isInView4 } = useInView({
    trackVisibility: true,
    delay: 100,
    rootMargin: "-44% 0% -44% 0%",
    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
  })

  // Create combined refs using useCallback
  const setRef1 = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        ;(ref1 as React.MutableRefObject<HTMLDivElement | null>).current = node
      }
      inViewRef1(node)
    },
    [inViewRef1]
  )

  const setRef2 = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        ;(ref2 as React.MutableRefObject<HTMLDivElement | null>).current = node
      }
      inViewRef2(node)
    },
    [inViewRef2]
  )

  const setRef3 = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        ;(ref3 as React.MutableRefObject<HTMLDivElement | null>).current = node
      }
      inViewRef3(node)
    },
    [inViewRef3]
  )

  const setRef4 = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        ;(ref4 as React.MutableRefObject<HTMLDivElement | null>).current = node
      }
      inViewRef4(node)
    },
    [inViewRef4]
  )

  useEffect(() => {
    if (isInView2) {
      setInitialLoad(false)
    }
  }, [isInView2])

  // Animate between states of the dot grid based on which statement
  // is on screen at the time. For mobile it should trigger as the
  // statement is 66% from the top, for desktop, when the top hits the center.
  useGSAP(() => {
    let mm = gsap.matchMedia(),
      breakPoint = 768

    mm.add(
      {
        isDesktop: `(min-width: ${breakPoint}px)`,
        isMobile: `(max-width: ${breakPoint - 1}px)`
      },
      context => {
        let { isMobile } = context.conditions || {}
        const startString = isMobile ? "top center" : "top 66%"

        gsap.timeline({
          scrollTrigger: {
            scroller: scrollerRef.current,
            start: startString,
            trigger: ref1.current,
            onEnter: () => {
              setDotFieldIndex("home-1")
            }
          }
        })

        gsap.timeline({
          scrollTrigger: {
            scroller: scrollerRef.current,
            start: startString,
            trigger: ref2.current,
            onEnter: () => {
              setDotFieldIndex("home-2")
            },
            onLeaveBack: () => {
              setDotFieldIndex("home-1")
            }
          }
        })

        gsap.timeline({
          scrollTrigger: {
            scroller: scrollerRef.current,
            start: startString,
            trigger: ref3.current,
            onEnter: () => {
              setDotFieldIndex("home-3")
            },
            onLeaveBack: () => {
              setDotFieldIndex("home-2")
            }
          }
        })

        gsap.timeline({
          scrollTrigger: {
            scroller: scrollerRef.current,
            start: startString,
            trigger: ref4.current,
            onEnter: () => {
              setDotFieldIndex("home-4")
            },
            onLeaveBack: () => {
              setDotFieldIndex("home-3")
            }
          }
        })

        gsap.timeline({
          scrollTrigger: {
            scroller: scrollerRef.current,
            start: startString,
            trigger: ref5.current,
            onEnter: () => {
              setDotFieldIndex("home-5")
            },
            onLeaveBack: () => {
              setDotFieldIndex("home-4")
            }
          }
        })
      }
    )
  })

  const isInView1 =
    isInView1Original || [isInView1Original, isInView2, isInView3, isInView4].every(bool => !bool)

  return (
    <PageContainer ref={scrollerRef} page="home">
      <Statement
        ref={setRef1}
        current={dotFieldIndex === "home-1" ? "" : null}
        style={{
          opacity: isInView1 || initialLoad ? 1 : 0.25,
          transition: "opacity 0.2s ease-in-out"
        }}
      >
        <h2>The best part of company building is the early&nbsp;days.</h2>
        <h2>
          You&nbsp;get to spend all your time talking to customers. Every&nbsp;hour of
          every&nbsp;day.
        </h2>
      </Statement>
      <Statement
        ref={setRef2}
        current={dotFieldIndex === "home-2" ? "" : null}
        style={{
          opacity: isInView2 ? 1 : 0.25,
          transition: "opacity 0.2s ease-in-out"
        }}
      >
        <h2>
          I always knew all my earliest customers by&nbsp;name. If&nbsp;they wanted something I
          would build it. We&nbsp;worked through things together. At&nbsp;Hubspot.
          At&nbsp;Performable. At&nbsp;Drift. If&nbsp;they had a problem, they&apos;d call&nbsp;me.
        </h2>
        <h2>This is customer&nbsp;success.</h2>
      </Statement>
      <Statement
        ref={setRef3}
        current={dotFieldIndex === "home-3" ? "" : null}
        style={{
          opacity: isInView3 ? 1 : 0.25,
          transition: "opacity 0.2s ease-in-out"
        }}
      >
        <h2>
          But&nbsp;eventually you grow. So&nbsp;you hire people to manage your&nbsp;customers.
        </h2>
        <h2>
          As&nbsp;the company grows, you get further from your customers&mdash;the people that
          you&apos;re in business to serve in the first&nbsp;place.
        </h2>
      </Statement>
      <Statement
        ref={setRef4}
        current={dotFieldIndex === "home-4" ? "" : null}
        style={{
          opacity: isInView4 ? 1 : 0.25,
          transition: "opacity 0.2s ease-in-out"
        }}
      >
        <h2>That&apos;s why we created&nbsp;Agency.</h2>
        <h2>
          Agency&nbsp;lets you treat every customer like they&apos;re your first&nbsp;customer.
        </h2>
      </Statement>
      <EliasSignoff ref={ref5} />
    </PageContainer>
  )
}
